import type { CreateComparisonExport } from "../schemas/comparison-exports.js";

import { comparisonFieldLabel } from "./field-labels.js";
import { projectReport, type ComparisonCharge } from "./report.js";

export type FrozenComparison = {
  audience: "advisor" | "client";
  language: "fr" | "en";
  headers: string[];
  rows: string[][];
  warning: string;
  spreadsheet: { headers: string[]; rows: string[][]; types: ("texte" | "nombre")[][] };
};
const statusLabels = {
  fr: {
    offered: "Offert",
    not_offered: "Non offert",
    not_provided: "Non fourni",
    unknown: "Inconnu",
    to_verify: "À vérifier",
    unreadable: "Illisible",
  },
  en: {
    offered: "Offered",
    not_offered: "Not offered",
    not_provided: "Not provided",
    unknown: "Unknown",
    to_verify: "To verify",
    unreadable: "Unreadable",
  },
};
function text(value: unknown): string {
  return value == null ? "" : typeof value === "object" ? JSON.stringify(value) : String(value);
}
/** Same source discriminator in selection, ordering and both artifacts; no internal UUID. */
export function comparisonOfferLabel(
  offer: NonNullable<ComparisonCharge["submissions"]>[number],
  sourceIndex: number,
  language: "fr" | "en",
) {
  const date = offer.received_at || offer.created_at;
  const suffix = date
    ? ` — ${offer.received_at ? (language === "fr" ? "reçue" : "received") : language === "fr" ? "ajoutée" : "added"} ${date.replace("T", " ").replace(".000Z", "Z").replace(/Z$/, " UTC")}`
    : "";
  return `${offer.insurer_name}${suffix} · ${language === "fr" ? "offre" : "offer"} ${sourceIndex + 1}`;
}
/** One authoritative projection per column prevents same-insurer offers from colliding. */
export function freezeComparison(
  charge: ComparisonCharge,
  input: CreateComparisonExport,
): FrozenComparison {
  const submissions = input.submission_ids.map((id) =>
    charge.submissions?.find((s) => s.id === id),
  );
  if (submissions.some((s) => !s)) throw new Error("INVALID_SELECTION");
  const offerLabels = submissions.map((s) =>
    comparisonOfferLabel(
      s!,
      charge.submissions!.findIndex((item) => item.id === s!.id),
      input.language,
    ),
  );
  const projections = submissions.map((s) =>
    projectReport({ ...charge, submissions: [s!] }, input.audience),
  );
  const available = new Set(projections.flatMap((p) => p.rows.map((r) => r.field_key)));
  if (input.field_keys.some((key) => !available.has(key))) throw new Error("INVALID_SELECTION");
  const fr = input.language === "fr";
  const spreadsheet: FrozenComparison["spreadsheet"] = {
    headers: [
      fr ? "Garantie / critère" : "Coverage / criterion",
      ...submissions.flatMap((s, i) =>
        (fr
          ? ["Valeur", "Statut", "Unité / période", "Annotation"]
          : ["Value", "Status", "Unit / period", "Annotation"]
        ).map((label) => `${i + 1}. ${offerLabels[i]} — ${label}`),
      ),
    ],
    rows: [],
    types: [],
  };
  const rows = input.field_keys.map((key) => {
    const label = comparisonFieldLabel(key, input.language);
    const sheetRow = [label];
    const types: ("texte" | "nombre")[] = ["texte"];
    const row = [
      label,
      ...projections.map((p, index) => {
        const source = p.rows.find((r) => r.field_key === key);
        const column = p.columns[0]!;
        const cell =
          source?.cells[
            input.audience === "advisor" ? submissions[index]!.id : column.insurer_code
          ];
        const status = cell
          ? statusLabels[input.language][cell.readable]
          : fr
            ? "Non fourni"
            : "Not provided";
        const notes: string[] = [];
        if (cell?.needs_review) notes.push(fr ? "À vérifier" : "To verify");
        if (input.audience === "advisor") {
          if (cell?.human_override) notes.push(fr ? "Correction humaine" : "Human correction");
          if (cell?.evidence) notes.push(text(cell.evidence));
        }
        const value = text(cell?.value),
          unit = cell?.unit ?? "",
          note = notes.join(" · ");
        sheetRow.push(value, status, unit, note);
        types.push(
          typeof cell?.value === "number" && Number.isFinite(cell.value) ? "nombre" : "texte",
          "texte",
          "texte",
          "texte",
        );
        return [status, value, unit, note].filter(Boolean).join(" · ");
      }),
    ];
    spreadsheet.rows.push(sheetRow);
    spreadsheet.types.push(types);
    return row;
  });
  return {
    audience: input.audience,
    language: input.language,
    headers: [
      fr ? "Garantie / critère" : "Coverage / criterion",
      ...submissions.map((s, i) => `${i + 1}. ${offerLabels[i]}`),
    ],
    rows,
    spreadsheet,
    warning: fr
      ? "Sélection explicite. Valeurs, devises, périodes et unités conservées sans conversion. Une donnée manquante ou à vérifier n’est pas un refus. Export figé, sans certification d’archive juridique."
      : "Explicit selection. Values, currencies, periods and units preserved without conversion. Missing or unverified data is not a refusal. Frozen export, without legal archive certification.",
  };
}
